import type { Hero } from '@mezha/api'
import { useEaseInjuryMutation, useHealInjuryMutation } from '../../api/endpoints/hero'
import { useCommand } from '../../lib/useCommand'
import { countdown, useRefetchAt, useServerNow } from '../../lib/time'
import { Emblem } from '../../ui/brand/Emblems'
import { ActionButton, PlankButton, cx } from '../../ui'
import './injury.css'

const noop = () => undefined

/** A short chip of the injury for the HUD: «травма · 12 мин», red while heavy. */
export function InjuryChip({ hero }: { hero: Hero }) {
  const now = useServerNow(1000)
  const inj = hero.injury
  if (!inj) return null
  const heavy = Boolean(inj.heavy && inj.heavy_until && countdown(now, inj.heavy_until).ms > 0)
  const left = countdown(now, heavy && inj.heavy_until ? inj.heavy_until : inj.until)
  return <small className={cx('injury-chip', heavy && 'is-heavy')} title={heavy ? 'Тяжёлая травма: ни идти, ни драться' : `Травма: −${inj.weaken_pct}% в бою`}>
    <Emblem name={heavy ? 'injury.heavy' : 'injury.light'} size={12} />{heavy ? 'тяжёлая травма' : 'травма'} · {left.text}</small>
}

/** The injury after a defeat (owner 28.09.2026): how long it lasts, what it
 *  takes away and — inside a healer's place — the healer's help: ease it once
 *  (half of what is left, free) or heal it at once for silver. */
export function InjuryCard({ hero, placeId, onChange }: { hero: Hero; placeId: string | null; onChange?: () => void }) {
  const now = useServerNow(1000)
  const [heal] = useHealInjuryMutation()
  const [ease] = useEaseInjuryMutation()
  const cmd = useCommand(onChange)
  const inj = hero.injury
  useRefetchAt(inj?.heavy_until ?? inj?.until, onChange ?? noop)
  if (!inj) return null
  const healer = inj.healers.find(h => h.place_id === placeId) ?? null
  const heavy = inj.heavy && inj.heavy_until && countdown(now, inj.heavy_until).ms > 0
  const left = countdown(now, inj.until)
  const nearest = inj.healers[0]
  return (
    <section className={cx('injury parch', heavy && 'injury--heavy')} aria-label="Травма">
      <h2 className="injury__title"><Emblem name={heavy ? 'injury.heavy' : 'injury.light'} size={18} />{heavy ? 'Тяжёлая травма' : 'Травма'}</h2>
      {heavy && inj.heavy_until
        ? <p>Идти и драться нельзя ещё <b>{countdown(now, inj.heavy_until).text}</b>, потом — лёгкая травма до конца, всего <b>{left.text}</b>.</p>
        : <p>Ещё <b>{left.text}</b>: в бою здоровья и силы удара меньше на {inj.weaken_pct}%.</p>}
      {healer ? (
        <>
          <p className="injury__who">{healer.name} может помочь.</p>
          {cmd.text && <p className="injury__error" role="alert">{cmd.text}</p>}
          <div className="injury__actions">
            <PlankButton locked={inj.eased} onClick={() => void cmd.run(() => ease({ expected_version: hero.version }).unwrap())}>
              {inj.eased ? 'Уже облегчали' : 'Облегчить · время вдвое, бесплатно'}
            </PlankButton>
            <ActionButton gold onPress={() => cmd.run(() => heal({ expected_version: hero.version }).unwrap())}>
              <Emblem name="res.silver" size={16} />Вылечить · {inj.heal_price} серебра
            </ActionButton>
          </div>
        </>
      ) : nearest && <p className="injury__who">Лечат: {inj.healers.map(h => `${h.name} (${h.place_name})`).join(', ')}.</p>}
    </section>
  )
}
